export const assortmentSimulationEn = {
  pageTitle: "Assortment Simulation",
  pageSubtitle:
    "Edit and optimize simulations with AI recommendations for major and minor review, and generate planograms",

  banner: {
    title: "Turn Simulations into Shelf-Ready Decisions",
    description:
      "Start by reviewing your pending simulations, or revisit previously used ones for reference. Explore the impact of each, click View/Edit to unlock the assortment details and fine-tune them, then check how your changes affect planogram utilization. When you're ready, update the planograms across the stores you need.",
  },

  columns: {
    simulation: "Simulation",
    category: "Category",
    stores: "Stores",
    geoLevel: "Segmentation",
    impact: "Impact",
    skuChanges: "SKU Changes",
    created: "Created",
    createdBy: "Created By",
    status: "Status",
    reviewType: "Review Type",
    actions: "Actions",
  },

  segmentationTypes: {
    General: "General",
    Cluster: "Cluster Institutional",
    Store: "Store",
    Unknown: "Unknown",
    "Cluster Category": "Cluster Category",
    "Cluster Institutional": "Cluster Institutional",
  },

  search: "Search simulations...",

  filters: {
    segmentation: "Segmentation",
    allSegmentations: "All Segmentations",
    category: "Category",
    allCategories: "All Categories",
    store: "Store",
    allStores: "All Stores",
    type: "Type",
    allTypes: "All Types",
    major: "Major",
    minor: "Minor",
    status: "Status",
    allStatuses: "All",
    activeFilters: "Active filters:",
    searchLabel: "Search",
    clearAll: "Clear all",
  },

  status: {
    pending: "Pending",
    inProgress: "In progress",
    used: "Used",
    failed: "Failed",
    unknown: "Unknown",
  },

  simulation: {
    recommendation: "recommendation",
    recommendations: "recommendations",
    added: "Added:",
    removed: "Removed:",
    net: "Net:",
    update: "Update",
    viewEdit: "View / Edit",
  },

  results: {
    showing: "Showing {{start}}-{{end}} of {{total}} simulations",
  },

  pagination: {
    rowsPerPage: "Rows per page:",
  },

  noResults: "No simulations found",
  noResultsDesc: "Create a simulation from Performance Analysis to get started",
  loading: "Loading simulations",
  loadError: "Unable to load simulations",
  loadErrorDesc: "Refresh the page or try again in a moment.",

  placementModal: {
    title: "Choose Placement Method",
    subtitle: "Choose how you want to generate the planogram arrangement",
    aiTitle: "AI Placement",
    aiDescription:
      "Let the AI automatically generate optimal product placement based on sales data, visual hierarchy, and category management best practices",
    recommended: "Recommended",
    infoTitle: "What happens next?",
    infoDescription:
      "Select your preferred placement method to proceed to the planogram generation step.",
  },

  merchConfig: {
    title: "Merchandising Configuration",
    subtitle: "Configure merchandising rules for the planogram update",
    merchandisingLabel: "Merchandising",
    toggles: {
      breakCases: "Can we break cases / trays if a core range product cannot fit otherwise?",
      fillExtraSpace: "Can we fill extra space next to cases / trays with loose products?",
      squeezePlacedProducts:
        "Can we squeeze already placed products to make room for new products?",
      leverageAlternative:
        "Can we leverage alternative orientations to fill empty space on a shelf?",
      verticalOverlap: "Can products overlap with shelves vertically?",
    },
    facingsStrategy: {
      title: "Facings Adjustment Strategy",
      rebalanceAll: "Rebalance Always",
      rebalanceAllDesc:
        "Always rebalance facings across all shelves, regardless of whether update operations are performed",
      affectedOnly: "Affected Shelves",
      affectedOnlyDesc: "Only rebalance facings on shelves affected by the update operations",
      emptySpaceOnly: "Empty Space",
      emptySpaceOnlyDesc:
        "Only add/rebalance facings to fill available empty space without removing existing facings",
      none: "None",
      noneDesc: "Do not perform any facings adjustment",
    },
    adjustFacingsMode: {
      title: "Facings Target Mode",
      exactMatch: "Exact Match",
      exactMatchDesc:
        "Match the target number of facings exactly and return errors when it is not achievable.",
      bestFit: "Best Fit",
      bestFitDesc:
        "Get as close as possible to the target facings while respecting shelf constraints.",
    },
    startUpdate: "Start Update",
  },

  updateModal: {
    title: "Update Planograms",
    subtitle: "The planograms will be updated with the current assortment changes",
    preparing: "Preparing update...",
    preparingDesc: "Checking for existing planograms and preparing the update process.",
    readyTitle: "Ready to Update",
    readyDesc: "Found existing merchflow with {{count}} store planograms ready for update.",
    linkedTitle: "Already Linked",
    linkedDesc:
      "This simulation is already linked to a merchflow with {{count}} store planograms. You can re-apply the update.",
    createdTitle: "Merchflow Created",
    createdDesc: "Created a new merchflow with {{count}} store planograms from PSA data.",
    noPsaTitle: "No PSA Data Available",
    noPsaDesc:
      "No PSA data has been loaded for this category. Upload PSA files through the ETL pipeline or the OWA import flow before running a minor review update.",
    noPogYetTitle: "No POG Yet for This Category",
    noPogYetDesc:
      "PSA data exists for the selected scope but no planogram (POG) has been generated yet. Run the merchflow trigger (frogger) from the Space Planning flow first, then return here to apply simulation changes.",
    noPogSnapshotTitle: "No planogram snapshot available",
    noPogSnapshotDesc:
      "This simulation does not contain the planogram snapshot required for a safe update. Re-analyze the current planograms and generate a new simulation before trying again.",
    invalidMasterDataTitle: "Planogram data must be fixed before applying",
    invalidMasterDataDesc:
      "No stores can be updated because required product or parent-planogram dimensions are missing or invalid. Correct the listed master data and regenerate the affected planograms before retrying.",
    invalidMasterDataStoreDetail: "Store {{store}}: {{products}} {{remaining}}",
    additionalItems: "+{{count}} more",
    missingStoreCodesWarning:
      "{{count}} selected store(s) have no planogram for this category and were skipped: {{stores}}",
    blockedStoresWarning:
      "{{count}} store(s) excluded due to dimension mismatch. Fix the fixture data and regenerate the planogram to unblock them.",
    blockedStoresStores: "Affected stores: {{stores}}",
    blockedStoresCodes: "Affected products: {{codes}}",
    availableStoreCodesHint: "PSA is available for {{count}} store(s): {{stores}}",
    categoryMismatchTitle: "Category not found in merchant catalog",
    categoryMismatchIntro: "The analytics category",
    categoryMismatchIntroTail: " has no matching row in the merchant category table.",
    categoryMismatchAction:
      "Create the category (or an alias) in the merchant catalog before triggering a minor review.",
    applyingTitle: "Applying Update...",
    applyingDesc:
      "Applying simulation recommendations to store planograms. This may take a moment.",
    errorTitle: "Update Failed",
    errorDesc: "An error occurred while preparing the update: {{message}}",
    unexpectedStatus: "Unexpected update status: {{status}}",
    unknownError: "An unknown error occurred.",
    cancel: "Cancel",
    update: "Apply Update",
    close: "Close",
    staleSnapshotTitle: "POG snapshot may be out of date",
    staleSnapshotDesc:
      "POG has been updated for {{count}} store(s) since analysis. Recommendations may not apply cleanly. Re-analyze?",
    ospDriftedTitle: "Shelf state changed since analysis",
    ospDriftedDesc:
      "Parent planograms for {{count}} store(s) were updated by another apply since this simulation was generated. Re-analyze to align recommendations with the current shelf.",
    reanalyze: "Re-analyze",
    continueToSpacePlanning: "Continue Anyway",
    alreadyAppliedTitle: "Already Applied",
    alreadyAppliedDesc:
      "This simulation has already been applied. View the resulting planogram in Space Planning instead of re-applying.",
    viewAppliedPlanogram: "View Applied Planogram",
    lineageStrandedTitle: "Planogram lineage is no longer available",
    lineageStrandedDesc:
      "The source planogram chain for this simulation can no longer be resolved. Re-analyze against the current planograms before applying again.",
    partialSuccessTitle: "Planograms updated with exclusions",
    partialSuccessDesc:
      "The update was created for eligible stores and products, but the items below were skipped.",
    partialSuccessSkippedProducts: "{{count}} skipped product(s):",
    partialSuccessProductDetail: "{{code}} — {{reason}}",
    partialSuccessStaleStores:
      "{{count}} store(s) were skipped because their planogram snapshot changed:",
    skippedProductReasons: {
      unit_depth_invalid: "unit depth is missing or invalid",
      parent_osp_geometry_invalid: "product dimensions do not fit the parent shelf geometry",
    },
    viewSpacePlanning: "View Space Planning",
  },
} as const;

export type AssortmentSimulationTranslations = typeof assortmentSimulationEn;
